import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  HardHat,
  Users,
  MapPin,
  BarChart3,
  Clock,
  ShieldCheck,
  Check,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import logo from "@/assets/logo.webp";
import { supabase } from "@/integrations/supabase/client";
import { trackLeadSubmission } from "@/lib/analytics";

const URL = "https://busditeads.com/construction-workforce-management-software";
const TITLE = "Construction Workforce Management Software — Business Dite Ads";
const DESC =
  "Plan crews, track GPS hours, allocate labor cost per job and run payroll — workforce management software built for construction companies. Start free.";

const faqs = [
  {
    q: "What is construction workforce management software?",
    a: "A single platform to schedule crews, track time on the job site, control labor cost per project, and run payroll — replacing spreadsheets, paper timesheets and disconnected apps.",
  },
  {
    q: "Can workers clock in from the job site?",
    a: "Yes. Crews clock in and out from their own phone with GPS verification — no app install required. Foremen and the office see hours in real time.",
  },
  {
    q: "Does it support hourly and daily pay rates?",
    a: "Both. Set hourly rates for some employees and daily (diária) rates for others. Payroll calculates each one correctly and allocates the cost to the right job.",
  },
  {
    q: "Will I see labor cost per project?",
    a: "Yes. Every hour or day worked is tied to a job, so you see real labor cost vs. revenue per project — and catch unprofitable jobs before they cost you money.",
  },
];

export const Route = createFileRoute("/construction-workforce-management-software")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  email: z.string().trim().email("Invalid email").max(255),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  phone: z.string().trim().max(50).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

const features = [
  {
    icon: Users,
    title: "Crew scheduling",
    description: "Assign crews to job sites, balance workloads and see who is where in real time.",
  },
  {
    icon: MapPin,
    title: "GPS clock-in",
    description:
      "Workers clock in from their phone with GPS verification — no app install, no paper timesheets.",
  },
  {
    icon: Clock,
    title: "Hourly or daily pay",
    description:
      "Pay some workers by the hour, others by the day. Payroll handles both and ties cost to the job.",
  },
  {
    icon: BarChart3,
    title: "Labor cost per job",
    description:
      "Every hour worked is linked to a project, so you see real labor cost vs. revenue per job.",
  },
  {
    icon: HardHat,
    title: "Built for the field",
    description: "Mobile-first for crews, full dashboard for the office. Works on any device.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance ready",
    description:
      "Audit trail on every entry, role-based access and secure cloud storage for tax season.",
  },
];

const benefits = [
  "No credit card required",
  "Setup in under 1 hour",
  "Free onboarding call",
  "Cancel anytime",
];

const socialProof = [
  "Trusted by residential and commercial contractors across the US, Brazil and Spain",
  "Replace 4–5 disconnected tools (paper, WhatsApp, spreadsheets, time apps) with one platform",
  "Save hours per week on payroll prep — every entry is already tied to a job",
  "Stop overpaying for ghost hours — GPS clock-in cuts time theft",
  "Catch unprofitable jobs while they're still in progress",
  "Available in English, Spanish and Portuguese",
];

function Page() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 sticky top-0 z-40 bg-background/80 backdrop-blur">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Business Dite Ads" className="h-8 w-8 rounded" />
            <span className="font-semibold">Business Dite Ads</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/pricing">
              <Button variant="ghost" size="sm">
                Pricing
              </Button>
            </Link>
            <Link to="/auth">
              <Button size="sm">Start free trial</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block text-xs font-medium px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">
            Construction Workforce Management Software
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5">
            Manage every crew, hour and labor dollar from one platform
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Business Dite Ads is workforce management software built for construction companies —
            GPS time tracking, crew scheduling, labor cost per job and payroll, in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth">
              <Button size="lg" className="gap-2">
                Start 14-day free trial <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <a href="#contact">
              <Button size="lg" variant="outline">
                Talk to us
              </Button>
            </a>
          </div>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16 border-t border-border/40">
        <h2 className="text-3xl font-bold text-center mb-3">
          Everything you need to manage your construction workforce
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          From the first clock-in to payroll day — every step ties back to the job, the crew and the
          cost.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f) => (
            <Card key={f.title} className="p-6">
              <f.icon className="h-8 w-8 text-primary mb-3" />
              <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section className="container mx-auto px-4 py-16 border-t border-border/40">
        <h2 className="text-2xl font-bold text-center mb-8">
          Why construction companies switch to Business Dite Ads
        </h2>
        <ul className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {socialProof.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm">
              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" /> <span>{p}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16 border-t border-border/40">
        <h2 className="text-3xl font-bold text-center mb-10">Frequently asked questions</h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((f) => (
            <Card key={f.q} className="p-5">
              <h3 className="font-semibold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground">{f.a}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact form */}
      <ContactSection />

      <footer className="border-t border-border/40 py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Business Dite Ads ·{" "}
        <Link to="/pricing" className="underline">
          Pricing
        </Link>{" "}
        ·{" "}
        <Link to="/" className="underline">
          Home
        </Link>
      </footer>
    </div>
  );
}

function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("marketing_leads").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company || null,
      phone: parsed.data.phone || null,
      message: parsed.data.message || null,
      source: "construction-workforce-management-software",
      locale: "en",
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not send. Please try again.");
      return;
    }
    setDone(true);
    trackLeadSubmission({
      source: "construction-workforce-management-software",
      locale: "en",
    });
    toast.success("Thanks — we'll be in touch shortly.");
  }

  return (
    <section id="contact" className="container mx-auto px-4 py-20 border-t border-border/40">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Talk to a workforce specialist</h2>
          <p className="text-muted-foreground">
            Tell us about your crews and we'll show you how Business Dite Ads fits — in a 20-minute
            call, no slides.
          </p>
        </div>

        {done ? (
          <Card className="p-8 text-center">
            <Check className="h-10 w-10 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">Message received</h3>
            <p className="text-sm text-muted-foreground">
              We'll reach out within one business day.
            </p>
          </Card>
        ) : (
          <Card className="p-6 md:p-8">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Work email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    maxLength={255}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    maxLength={200}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    maxLength={50}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message">How many workers / what are you using today?</Label>
                <Textarea
                  id="message"
                  rows={4}
                  maxLength={2000}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
              <Button type="submit" size="lg" className="w-full gap-2" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Request a demo
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                By submitting you agree to be contacted about Business Dite Ads. We never share your
                data.
              </p>
            </form>
          </Card>
        )}
      </div>
    </section>
  );
}
